import { Component, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { PageTitleService } from '@employer/app/services/page-title.service'

export interface StubPageRouteData {
  title: string
  icon: string
  description: string
  screenshotPath?: string
  implemented?: false
  /** Story 4.5: optional `data-tour-id` this stub page carries (only the dashboard route sets it). */
  tourId?: string
}

/**
 * Stub page rendered for any new-IA route whose feature isn't built in cnect-web yet.
 *
 * Story 3.1. The component reads its content from the route's `data` field:
 *
 * ```ts
 * { path: 'company/profile', component: StubPageComponent,
 *   data: { title: 'Company Profile', icon: 'business',
 *           description: 'Manage your company profile, identity, and public-facing info.',
 *           screenshotPath: 'assets/stubs/company-profile.png', implemented: false } }
 * ```
 *
 * The page renders the configured cnect-ux screenshot as a dimmed/blurred backdrop
 * (opacity 0.35, blur 3px) with a centered overlay card showing the icon + title +
 * description + "Not implemented yet" caption.
 *
 * The shell chrome (sidebar, header, footer) renders normally — the stub is page-body-only.
 *
 * NgModule-based per project convention (NOT standalone).
 */
@Component({
  selector: 'app-stub-page',
  template: `
    <div
      class="stub-page e11-relative e11-w-full e11-h-full e11-min-h-[60vh] e11-flex e11-items-center e11-justify-center e11-overflow-hidden"
      [attr.data-tour-id]="tourId"
    >
      <div
        *ngIf="screenshotPath as src"
        class="stub-page__backdrop e11-absolute e11-inset-0 e11-bg-no-repeat e11-bg-center e11-bg-cover"
        [style.background-image]="'url(' + src + ')'"
      ></div>
      <div
        class="stub-page__card e11-relative e11-z-10 e11-max-w-[480px] e11-w-full e11-mx-6 e11-p-8 e11-bg-skin-white e11-rounded-lg e11-shadow-xl e11-flex e11-flex-col e11-items-center e11-text-center"
      >
        <span class="material-symbols-outlined e11-text-skin-primary-accent e11-text-5xl e11-mb-4">{{ icon }}</span>
        <h2 class="e11-text-skin-primary e11-mb-2">{{ title }}</h2>
        <p class="e11-text-skin-grey e11-mb-4">{{ description }}</p>
        <p class="e11-text-skin-grey/70 e11-text-sm e11-italic">Not implemented yet — coming soon.</p>
      </div>
    </div>
  `,
  styles: [
    `
      .stub-page__backdrop {
        opacity: 0.35;
        filter: blur(3px);
      }
    `,
  ],
})
export class StubPageComponent implements OnInit {
  title = ''
  icon = 'hourglass_empty'
  description = ''
  screenshotPath?: string
  tourId?: string

  constructor(private route: ActivatedRoute, private pageTitle: PageTitleService) {}

  ngOnInit(): void {
    const data = this.route.snapshot.data as Partial<StubPageRouteData>
    this.title = data.title ?? 'Coming soon'
    this.icon = data.icon ?? 'hourglass_empty'
    this.description = data.description ?? ''
    this.screenshotPath = data.screenshotPath
    this.tourId = data.tourId || undefined

    this.pageTitle.set(this.icon, this.title)
  }
}
